import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { Channel, ContentPieceDetail, ManagedPublication } from '../../shared/api/types';
import { Alert, Button, Card, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { CHANNEL, PUBLICATION_STATE } from './labels';

const when = (iso: string) => new Date(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/**
 * Publicación (Motor 23) de una pieza: se publica la última versión APROBADA, en la iglesia y/o
 * en la página pública, ahora o programada. Publicar de nuevo reemplaza la anterior del canal.
 */
export function PublishPanel({ churchId, piece, version }: { churchId: string; piece: ContentPieceDetail; version: number }) {
  const [reload, setReload] = useState(0);
  const [channels, setChannels] = useState<Set<Channel>>(new Set(['church']));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const list = useAsync(
    (signal) => api.get<{ items: ManagedPublication[] }>(`/publishing/churches/${churchId}/publications?pieceId=${piece.id}`, signal).then((r) => r.items),
    [churchId, piece.id, version, reload],
  );
  const toggle = (c: Channel) =>
    setChannels((s) => {
      const next = new Set(s);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });

  async function publish(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const at = String(new FormData(event.currentTarget).get('publishAt') ?? '');
    setBusy(true);
    setError(null);
    try {
      await api.post(`/publishing/churches/${churchId}/publications`, { pieceId: piece.id, channels: [...channels], publishAt: at ? new Date(at).toISOString() : null });
      setNotice(at ? `Programada para ${when(new Date(at).toISOString())}.` : 'Publicada.');
      setReload((r) => r + 1);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const live = list.status === 'ready' ? list.data.filter((p) => p.state !== 'withdrawn') : [];
  const past = list.status === 'ready' ? list.data.filter((p) => p.state === 'withdrawn') : [];

  return (
    <Card title="Publicación">
      {piece.approvedRevision === null ? (
        <p className="muted small">Se podrá publicar cuando una persona apruebe la pieza.</p>
      ) : piece.status === 'archived' ? (
        <p className="muted small">Restaura la pieza para volver a publicarla.</p>
      ) : (
        <form className="form" onSubmit={publish} noValidate>
          <fieldset className="choices choices--row">
            <legend>Publicar la versión {piece.approvedRevision} en</legend>
            {(Object.keys(CHANNEL) as Channel[]).map((c) => (
              <label key={c} className="choice">
                <input type="checkbox" checked={channels.has(c)} onChange={() => toggle(c)} />
                <span>{CHANNEL[c]}</span>
              </label>
            ))}
          </fieldset>
          {piece.approvedRevision !== piece.currentRevision ? (
            <Alert tone="info">La versión {piece.currentRevision} aún no está aprobada; se publicará la {piece.approvedRevision}.</Alert>
          ) : null}
          <Field label="Programar para (opcional)" name="publishAt" type="datetime-local" hint="Déjalo vacío para publicar ahora." />
          {notice ? <Alert tone="success">{notice}</Alert> : null}
          {error ? <Alert>{error}</Alert> : null}
          <Button type="submit" busy={busy} disabled={channels.size === 0}>
            Publicar
          </Button>
        </form>
      )}
      {list.status === 'loading' ? <Loading /> : null}
      {live.length > 0 || past.length > 0 ? (
        <ul className="list">
          {[...live, ...past].map((p) => (
            <PublicationRow key={p.id} churchId={churchId} publication={p} onChange={() => setReload((r) => r + 1)} />
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

function PublicationRow({ churchId, publication: p, onChange }: { churchId: string; publication: ManagedPublication; onChange: () => void }) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const state = PUBLICATION_STATE[p.state];

  async function withdraw(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/publishing/churches/${churchId}/publications/${p.id}/withdraw`, { reason: String(new FormData(event.currentTarget).get('reason') ?? '') });
      onChange();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <li className="list__item pos__line">
      <span>
        <strong>{CHANNEL[p.channel]}</strong>{' '}
        <span className="muted small">
          v{p.revision} · {p.state === 'withdrawn' && p.withdrawnAt ? `retirada ${when(p.withdrawnAt)}` : when(p.publishAt)}
          {p.withdrawReason ? ` · ${p.withdrawReason}` : ''}
        </span>
      </span>
      <span className={`badge ${state.tone}`}>{state.label}</span>
      {p.state !== 'withdrawn' && !asking ? (
        <Button variant="ghost" onClick={() => setAsking(true)}>
          Retirar
        </Button>
      ) : null}
      {asking ? (
        <form className="form" onSubmit={withdraw} noValidate>
          <Field label={`Motivo para retirar de ${CHANNEL[p.channel]}`} name="reason" required minLength={3} maxLength={300} />
          {error ? <Alert>{error}</Alert> : null}
          <div className="actions">
            <Button type="submit" variant="danger" busy={busy}>
              Retirar
            </Button>
            <Button type="button" variant="ghost" onClick={() => setAsking(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      ) : null}
    </li>
  );
}
